<script lang="ts">
export default {
  name: 'ConvertScratchTowerModal',
  components: {
    AppModal,
    AppSelect
  }
};
</script>

<script setup lang="ts">
import { ref, watch } from 'vue';
import AppModal from '@/components/v2/AppModal.vue';
import AppSelect from '@/components/v2/AppSelect.vue';
import { accountService } from '@/services/api';
import { AccountSummary } from '@/types/AccountSummary';
import { onMounted } from 'vue';
import { onUnmounted } from 'vue';

const title = 'Convert Tower';

const props = defineProps<{
  show: boolean;
  towerId?: string;
}>();

const emit = defineEmits<{
  (e: 'cancel'): void;
  (e: 'convert', towerId: string | undefined, accountId: number | undefined): void;
}>();

// -----
// refs
// -----
const accountId = ref<number>();
const accounts = ref<AccountSummary[]>([]);

// ---------
// watchers
// ---------
watch(
  () => props.show,
  (show) => {
    if (!show) {
      accountId.value = undefined;
    }
  },
);

// ---------
// lifecycles
// ---------
onMounted(async () => {
  addEventListener('keyup', handleCloseWithEscapeKey);
  accounts.value = await accountService.list();
});

onUnmounted(() => {
  removeEventListener('keyup', handleCloseWithEscapeKey);
});

// ---------
// handlers
// ---------
function handleCloseWithEscapeKey(e: KeyboardEvent): void {
  if (e.key === 'Escape') {
    emit('cancel');
  }
}
</script>

<template>
  <AppModal
    :show="show"
    :title="title"
    :handle-cancel="() => emit('cancel')"
    :handle-action="() => emit('convert', props.towerId, accountId)"
    action-button-label="Convert"
  >
    <template #content>
      <div class="tw:flex tw:flex-col tw:gap-3">
        <p class="tw:text-lg tw:text-gray-600">Choose the account you want to link to this tower.</p>
        <div class="tw:w-full">
          <label class="tw:label">
            <span class="tw:label-text tw:text-xs tw:text-gray-500"
              >Account <span class="tw:text-primary">*</span></span
            >
          </label>
          <AppSelect
            v-model="accountId"
            :options="accounts.map(account => ({ value: account.accountID, label: account.accountName }))"
            class="tw:w-full tw:h-9"
            placeholder="Select account"
          />
        </div>
      </div>
    </template>
  </AppModal>
</template>
